// ============================================
// TERMINOSCOTIZACION.JS — bloque "Terminos y condiciones" del editor de cotizacion
// ============================================
// Una casilla por clausula. Las FIJAS salen marcadas y bloqueadas ("Siempre se incluye").
// Lo que se ve aqui es lo que ira al PDF, al final de la cotizacion.
//
// De donde sale cada texto:
//   - Si la clausula YA esta guardada en esta cotizacion, se ensena la FOTO guardada (el texto
//     del dia en que entro), aunque el catalogo haya cambiado despues. Se avisa si es asi.
//   - Si se marca ahora, se ensena el texto VIGENTE del catalogo.
// El que decide de verdad es el servidor al guardar (services/terminosCotizacion.js); esta
// pantalla solo dice que se marco.
//
// `seleccion === null` = cotizacion anterior a los terminos: no se le añade nada por detras.
// Sale un boton para añadirlos a proposito.
//
// Responsive: una columna siempre; filas de 44 px minimo para el dedo (360 px incluido).

import React from 'react';
import { PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE, estiloTarjeta } from './helpersClientes';

const AZUL = '#265F97';
// Mismo criterio que services/terminosCotizacion.js (resolverTexto): con anticipo < 100 y texto
// parcial, se usa el parcial. Aqui es solo VISTA PREVIA con lo que hay en pantalla; al guardar,
// el texto que vale es el que devuelve el servidor.
function momentoSaldo(saldoDias) {
  const d = Number(saldoDias);
  return Number.isInteger(d) && d >= 1 ? `a ${d} dias naturales de la entrega del alcance contratado` : 'contra entrega del alcance contratado';
}
function resolver(plantilla, parcial, { vigenciaDias, anticipoPct, saldoDias }) {
  const d = Math.max(1, Math.trunc(Number(vigenciaDias) || 15));
  const n = Number(anticipoPct);
  const ant = Number.isFinite(n) ? Math.min(100, Math.max(0, Math.round(n))) : 100;
  const base = ant < 100 && parcial ? parcial : plantilla;
  return String(base || '')
    .replace(/\{vigencia_dias\}/g, String(d))
    .replace(/\{anticipo_pct\}/g, String(ant))
    .replace(/\{saldo_pct\}/g, String(100 - ant))
    .replace(/\{saldo_momento\}/g, momentoSaldo(saldoDias))
    .replace(/\{saldo_dias\}/g, Number(saldoDias) >= 1 ? String(Number(saldoDias)) : '0');
}

export default function TerminosCotizacion({
  catalogo = [],          // clausulas activas del catalogo
  foto = null,            // lo guardado en la cotizacion (array) o null
  seleccion = null,       // ids marcados, o null = cotizacion anterior sin terminos
  onCambiar,              // (ids) => void
  onActivar,              // () => void   (cotizacion anterior: añadir terminos a proposito)
  soloLectura = false,    // autorizada: se emitio asi
  vigenciaDias = 15,
  anticipoPct = 100,      // el anticipo que hay en pantalla: "Forma de pago" lo refleja al momento
  saldoDias = null,       // plazo del saldo en pantalla (null = contra entrega)
  duplicadaDeFolio = null,
  familiasTexto = '',     // "CCTV y videovigilancia" — por que vienen marcadas
  cargando = false,
}) {
  const fotoPorId = new Map((Array.isArray(foto) ? foto : []).map(t => [Number(t.clausula_id), t]));
  const catPorId = new Map(catalogo.map(c => [c.id, c]));

  // Union catalogo + foto (una clausula desactivada que esta en la foto se sigue viendo).
  const filas = [];
  for (const c of catalogo) filas.push({ id: c.id, titulo: c.titulo, plantilla: c.texto, parcial: c.texto_anticipo_parcial || null, fija: !!c.fija, orden: c.orden });
  for (const [id, t] of fotoPorId) {
    if (!catPorId.has(id)) filas.push({ id, titulo: t.titulo, plantilla: t.plantilla, parcial: t.plantilla_parcial || null, fija: !!t.fija, orden: t.orden, retirada: true });
  }
  filas.sort((a, b) => (Number(a.orden) - Number(b.orden)) || (a.id - b.id));

  const sel = new Set(seleccion || []);
  const total = filas.filter(f => sel.has(f.id) || f.fija).length;

  const alternar = (id) => {
    if (soloLectura || !onCambiar) return;
    const n = new Set(sel);
    if (n.has(id)) n.delete(id); else n.add(id);
    onCambiar([...n]);
  };

  const aviso = (txt, tono = 'info') => (
    <div style={{
      fontSize: 12.5, lineHeight: 1.45, padding: '8px 10px', borderRadius: 8, marginBottom: 10,
      background: tono === 'ambar' ? '#FFF6E5' : '#EEF6FF',
      border: `1px solid ${tono === 'ambar' ? '#F3D9A4' : '#CFE2F5'}`,
      color: tono === 'ambar' ? '#8a5a1b' : '#1F4E79',
    }}>{txt}</div>
  );

  return (
    <div style={{ ...estiloTarjeta, padding: 18, fontFamily: PP, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>Términos y condiciones</div>
        {seleccion !== null && (
          <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>{total} de {filas.length} incluidas</span>
        )}
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 12, lineHeight: 1.45 }}>
        Salen al final del PDF, después de los importes. Las fijas van siempre.
      </div>

      {seleccion === null ? (
        <div>
          {aviso('Esta cotización es anterior a los términos y condiciones, así que su PDF sale sin ellos. No se le añaden solos: si quieres que los lleve, añádelos.', 'ambar')}
          {!soloLectura && (
            <button type="button" onClick={onActivar} disabled={cargando}
              style={{ padding: '10px 16px', minHeight: 44, borderRadius: 10, border: `1px solid ${AZUL}`, background: '#fff', color: AZUL, fontFamily: PP, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', width: '100%', maxWidth: 360 }}>
              {cargando ? 'Cargando…' : 'Añadir términos y condiciones'}
            </button>
          )}
        </div>
      ) : (
        <>
          {soloLectura && aviso('Cotización autorizada: los términos quedan tal como se emitieron.')}
          {!soloLectura && duplicadaDeFolio && aviso(`Copia de ${duplicadaDeFolio}: lleva las mismas cláusulas que la original, pero con el texto VIGENTE del catálogo (no el de la original). Es una cotización nueva.`)}
          {!soloLectura && !duplicadaDeFolio && !foto && familiasTexto && aviso(`Vienen marcadas según el tipo de trabajo (${familiasTexto}). Puedes marcar o desmarcar las que no son fijas.`)}

          <div style={{ display: 'grid', gap: 8 }}>
            {filas.map(f => {
              const marcada = f.fija || sel.has(f.id);
              const guardada = fotoPorId.get(f.id);
              // Guardada: SU plantilla (la del dia en que entro), con el anticipo de ahora.
              const texto = guardada
                ? resolver(guardada.plantilla, guardada.plantilla_parcial, { vigenciaDias, anticipoPct, saldoDias })
                : resolver(f.plantilla, f.parcial, { vigenciaDias, anticipoPct, saldoDias });
              const cat = catPorId.get(f.id);
              const catalogoCambio = guardada && cat && (String(cat.texto) !== String(guardada.plantilla)
                || String(cat.texto_anticipo_parcial || '') !== String(guardada.plantilla_parcial || ''));
              const bloqueada = f.fija || soloLectura;
              return (
                <label key={f.id} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', minHeight: 44,
                  border: `1px solid ${marcada ? '#CFE2F5' : COLOR_BORDE}`, borderRadius: 10,
                  background: marcada ? '#F7FBFF' : '#fff', cursor: bloqueada ? 'default' : 'pointer', minWidth: 0,
                }}>
                  <input type="checkbox" checked={marcada} disabled={bloqueada} onChange={() => alternar(f.id)}
                    aria-label={`${f.titulo}${f.fija ? ' (siempre se incluye)' : ''}`}
                    style={{ width: 20, height: 20, marginTop: 1, flexShrink: 0, accentColor: AZUL }} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <strong style={{ fontSize: 13.5, color: COLOR_OSCURO }}>{f.titulo}</strong>
                      {f.fija && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: AZUL }}>Siempre se incluye</span>}
                      {f.retirada && <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#f4f5f6', color: COLOR_GRIS }}>Ya no está en el catálogo</span>}
                    </span>
                    <span style={{ display: 'block', fontSize: 12.5, color: COLOR_GRIS, lineHeight: 1.45, marginTop: 3, wordBreak: 'break-word' }}>{texto}</span>
                    {catalogoCambio && (
                      <span style={{ display: 'block', fontSize: 11.5, color: '#8a5a1b', marginTop: 4 }}>
                        El catálogo cambió después; esta cotización conserva el texto con el que se guardó.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
          {!soloLectura && (
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 10 }}>
              Se guardan con «Guardar cotización» (o al descargar el PDF).
            </div>
          )}
        </>
      )}
    </div>
  );
}
